'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import MapBackgroundClient from './MapBackgroundClient';
import PropertyPanel from './PropertyPanel';
import PropertyModal from './PropertyModal';
import AdBanner from './AdBanner';
import { mockProperties } from '../data/mockProperties';
import type { MapBounds, Property } from '../types/property';

export interface ProgressStep {
  phase: string;
  message: string;
}

type SearchState = 'idle' | 'searching' | 'complete';

function inBounds(p: Property, b: MapBounds): boolean {
  return p.lat <= b.north && p.lat >= b.south && p.lng >= b.west && p.lng <= b.east;
}

interface Props {
  sessionId: string | null;
  onSearchComplete?: () => void;
}

export default function MapView({ sessionId, onSearchComplete }: Props) {
  const [bounds, setBounds]                   = useState<MapBounds | null>(null);
  const [selectedPropertyId, setSelectedPropertyId] = useState<string | null>(null);

  // Search state
  const [searchState, setSearchState]         = useState<SearchState>('idle');
  const [searchProperties, setSearchProperties] = useState<Property[]>([]);
  const [progressSteps, setProgressSteps]     = useState<ProgressStep[]>([]);
  const [center, setCenter]                   = useState<{ lat: number; lng: number } | null>(null);
  const centerRef                             = useRef<{ lat: number; lng: number } | null>(null);
  const sourceRef                             = useRef<EventSource | null>(null);

  // Open SSE stream when sessionId is set
  useEffect(() => {
    if (!sessionId) return;

    // Reset previous search
    setSearchProperties([]);
    setProgressSteps([]);
    setSearchState('searching');

    const source = new EventSource(`/api/search/${sessionId}/stream`);
    sourceRef.current = source;

    source.addEventListener('location', (e) => {
      const { lat, lng } = JSON.parse(e.data);
      centerRef.current = { lat, lng };
      setCenter({ lat, lng });
    });

    source.addEventListener('status', (e) => {
      const { phase, message } = JSON.parse(e.data);
      setProgressSteps((prev) => [...prev, { phase, message }]);
    });

    source.addEventListener('property_preview', (e) => {
      const raw = JSON.parse(e.data);
      const normalizedUrl = raw.url?.replace(/\/$/, '') || '';
      setSearchProperties((prev) => {
        if (prev.some((p) => p.url === normalizedUrl || p.url === raw.url)) return prev;
        return [...prev, {
          id: `preview_${normalizedUrl}`,
          title: raw.title || 'Loading...',
          address: raw.address || '',
          postcode: raw.postcode || '',
          price: raw.price || 0,
          bedrooms: raw.bedrooms ?? 0,
          bathrooms: raw.bathrooms ?? 0,
          propertyType: raw.property_type ?? 'house',
          lat: 0,
          lng: 0,
          imageUrl: raw.image_url ?? '',
          url: raw.url,
          matchScore: 0,
          description: raw.description || '',
        }];
      });
    });

    source.addEventListener('property_found', (e) => {
      const raw = JSON.parse(e.data);
      const property: Property = {
        id: raw.id,
        title: raw.title,
        address: raw.address,
        postcode: raw.postcode,
        price: raw.price,
        bedrooms: raw.bedrooms ?? 0,
        bathrooms: raw.bathrooms ?? 0,
        propertyType: raw.property_type ?? 'house',
        lat: raw.lat ?? (centerRef.current?.lat ?? 51.5) + (Math.random() - 0.5) * 0.05,
        lng: raw.lng ?? (centerRef.current?.lng ?? -0.1) + (Math.random() - 0.5) * 0.05,
        imageUrl: raw.image_url ?? '',
        url: raw.url,
        matchScore: raw.match_score ?? 0,
        description: raw.description,
        matchingCriteria: raw.matching_criteria,
      };
      setSearchProperties((prev) => {
        // Replace any existing preview matched by URL
        const previewIdx = prev.findIndex((p) => p.id.startsWith('preview_') && p.url === property.url);
        if (previewIdx >= 0) {
          const updated = [...prev];
          updated[previewIdx] = property;
          return updated;
        }
        return [...prev, property];
      });
    });

    source.addEventListener('complete', () => {
      setSearchState('complete');
      onSearchComplete?.();
      source.close();
    });

    source.addEventListener('error', () => {
      setSearchState('complete');
      onSearchComplete?.();
      source.close();
    });

    return () => {
      source.close();
      sourceRef.current = null;
    };
  }, [sessionId]);

  const handleBoundsChange = useCallback((b: MapBounds) => {
    setBounds(b);
  }, []);

  const handleSelectProperty = useCallback((id: string) => {
    setSelectedPropertyId(id);
  }, []);

  const handleCloseModal = useCallback(() => {
    setSelectedPropertyId(null);
  }, []);

  // Which properties to show — keep search results after completion
  const hasSearchResults = searchState !== 'idle';
  const mapProperties = hasSearchResults ? searchProperties : mockProperties;
  const panelProperties = hasSearchResults
    ? searchProperties
    : bounds ? mockProperties.filter((p) => inBounds(p, bounds)) : [];

  const selectedProperty = selectedPropertyId
    ? mapProperties.find((p) => p.id === selectedPropertyId) ?? null
    : null;

  return (
    <>
      <MapBackgroundClient
        properties={mapProperties}
        selectedPropertyId={selectedPropertyId}
        onBoundsChange={handleBoundsChange}
        onSelectProperty={handleSelectProperty}
        center={center}
      />
      <PropertyPanel
        properties={panelProperties}
        selectedPropertyId={selectedPropertyId}
        onSelectProperty={handleSelectProperty}
        searchState={searchState}
        progressSteps={progressSteps}
      />
      {selectedProperty && (
        <PropertyModal property={selectedProperty} onClose={handleCloseModal} />
      )}
      {searchState === 'searching' && <AdBanner />}
    </>
  );
}
